/**
 * My Information — what the company holds about you, as you submitted it.
 *
 * ── Why this page exists ──────────────────────────────────────────────────────────────────
 *
 * The onboarding form is reached through an emailed invitation link, and that link is BURNED on
 * submit (see submitProfile). So the moment somebody finished filling it in, their own declared
 * address, bank account, nominees and identification numbers became invisible to them and
 * readable only by HR. "What does my employer have on file about me" is a question the person it
 * describes should not have to ask somebody else.
 *
 * ── Read-only, deliberately ───────────────────────────────────────────────────────────────
 *
 * Correcting this data is HR's to do — the record is what the employee declared, and letting
 * them silently rewrite a declaration after the fact would remove the point of having made one.
 * So this page shows and does not edit, and says where to go instead.
 *
 * ── Same rendering as HR sees ─────────────────────────────────────────────────────────────
 *
 * ProfileSections is shared with Settings → Employee Information, so the employee is looking at
 * exactly the fields HR is looking at. A second layout here would eventually differ, and the
 * difference would be invisible: something held about somebody that they cannot see.
 */

import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import MoodJournalTabs from '../moodmate/MoodJournalTabs';
import {
  EmployeeProfileService,
  PROFILE_BACKEND_UNAVAILABLE,
} from '../../services/employeeProfileService';
import {
  ChangeRequestStatus,
  CHANGE_STATUS_LABEL,
  EmployeeSelfServiceProfile,
  ProfileCompletionStatus,
  PROFILE_STATUS_LABEL,
} from '../../types/employeeProfile';
import ProfileSections, { withPath } from '../common/EmployeeProfileSections';
import { useToast } from '../common/Toast';
import { MOODMATE_ENABLED } from '../../config/features';
import './MyInformation.css';

const MyInformation: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { tab } = useParams<{ tab?: string }>();
  // The page hosts two sub-sections: the record itself, and the personal Mood
  // Journal. The tab lives in the URL (/my-information/mood-journal) so it
  // survives a refresh, matching how Attendance tabs work.
  // Mood Journal is gated per-environment (REACT_APP_ENABLE_MOODMATE). Where it's
  // off (e.g. prod) the tab doesn't exist, so a stale /my-information/mood-journal
  // URL just falls back to the Information view.
  const onMoodJournal = MOODMATE_ENABLED && tab === 'mood-journal';
  const { showSuccess, showError } = useToast();
  const [profile, setProfile] = useState<EmployeeSelfServiceProfile | undefined>();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [revealed, setRevealed] = useState(false);

  /*
   * The employee's proposed version, or null when they are just reading.
   *
   * Null except while requesting changes, so the read view always renders the APPROVED record.
   * That matters more here than on the HR screen: what this page shows must be what the company
   * holds, not what somebody has asked it to hold — otherwise an employee would believe a change
   * had taken effect the moment they typed it.
   */
  const [draft, setDraft] = useState<Partial<EmployeeSelfServiceProfile> | null>(null);
  const [sending, setSending] = useState(false);

  const email = user?.email || '';

  const load = useCallback(async () => {
    if (!email) {
      setLoading(false);
      return;
    }
    setLoading(true);
    setError('');
    try {
      setProfile(await EmployeeProfileService.get(email));
    } catch (err: any) {
      setError(err?.message || 'Your information could not be loaded.');
    } finally {
      setLoading(false);
    }
  }, [email]);

  useEffect(() => {
    // The Mood Journal tab doesn't need the employee profile, so don't fetch it
    // there — it keeps the journal usable even if the profile backend is down.
    if (!onMoodJournal) load();
  }, [load, onMoodJournal]);

  /**
   * Send the edits to HR.
   *
   * The whole draft goes; the SERVER works out which fields actually moved and files those as
   * pending. A diff computed here would be a second opinion about what changed, and the queue HR
   * rules on should be derived from the two versions the server holds.
   */
  const requestChanges = async () => {
    if (!draft) return;
    setSending(true);
    try {
      await EmployeeProfileService.proposeChanges(draft);
      showSuccess('Your changes have been sent to HR for approval.', 'Sent for approval');
      setDraft(null);
      await load();
    } catch (err: any) {
      // Left in edit mode on purpose — a failed send must not discard what was typed.
      showError(err?.message || 'Your changes could not be sent.', 'Not sent');
    } finally {
      setSending(false);
    }
  };

  const editing = draft !== null;
  const pending = (profile?.pendingChanges || []).filter(
    (change) => change.status === ChangeRequestStatus.PENDING
  );

  // Sub-section tabs: the record, and the personal Mood Journal. Shared between
  // the two views so they read identically.
  const sectionTabs = (
    <div className="myinfo__tabs" role="tablist" aria-label="My Information sections">
      <button
        type="button"
        role="tab"
        aria-selected={!onMoodJournal}
        className={`myinfo__tab ${!onMoodJournal ? 'is-active' : ''}`}
        onClick={() => navigate('/my-information')}
      >
        Information
      </button>
      {MOODMATE_ENABLED && (
        <button
          type="button"
          role="tab"
          aria-selected={onMoodJournal}
          className={`myinfo__tab ${onMoodJournal ? 'is-active' : ''}`}
          onClick={() => navigate('/my-information/mood-journal')}
        >
          Mood Journal
        </button>
      )}
    </div>
  );

  // The Mood Journal is a self-contained, self-scoped section — it does not depend
  // on the employee profile, so it renders regardless of profile load state.
  if (onMoodJournal) {
    return (
      <div className="myinfo">
        <div className="myinfo__header">
          <div className="myinfo__heading">
            <h2>My Information</h2>
            <p>View the personal, professional and statutory information submitted by you.</p>
          </div>
        </div>
        {sectionTabs}
        <MoodJournalTabs />
      </div>
    );
  }

  if (loading) {
    return <div className="myinfo__empty">Loading your information…</div>;
  }

  /*
   * An account with no email has nothing to look up.
   *
   * The federated Super Admin is created by Azure with no email attribute, and the profile store
   * is keyed on one — so there is no record to find rather than an empty one to show.
   */
  if (!email) {
    return (
      <div className="myinfo">
        <div className="myinfo__header"><h2>My Information</h2></div>
        <p className="myinfo__empty">
          This account has no email address on it, so there is no employee record to show.
        </p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="myinfo">
        <div className="myinfo__header"><h2>My Information</h2></div>
        <p className="myinfo__error">
          {error === PROFILE_BACKEND_UNAVAILABLE
            ? 'Employee information is not available in this environment yet.'
            : error}
        </p>
      </div>
    );
  }

  const initials = (user?.name || email)
    .split(/[\s.@]+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase() || '')
    .join('');

  return (
    <div className="myinfo">
      {/*
        * Title, and beside it the state of the record plus the one action on the page.
        *
        * They were three separate rows — heading, then a card holding the pill, then the button —
        * which put the control furthest from the thing it acts on and left the status looking
        * like a section of its own.
        */}
      <div className="myinfo__header">
        <div className="myinfo__heading">
          <h2>My Information</h2>
          <p>View the personal, professional and statutory information submitted by you.</p>
        </div>

        {profile && (
          <div className="myinfo__header-actions">
            <StatusPill status={profile.status} />

            {!editing && (
              <button
                type="button"
                onClick={() => setDraft(profile)}
                className="eps__btn"
              >
                Request changes
              </button>
            )}

            {editing && (
              <div className="myinfo__actions">
                <button
                  type="button"
                  onClick={() => setDraft(null)}
                  disabled={sending}
                  className="eps__btn"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={requestChanges}
                  disabled={sending}
                  className="eps__btn eps__btn--primary"
                >
                  {sending ? 'Sending…' : 'Send to HR for approval'}
                </button>
              </div>
            )}
          </div>
        )}
      </div>

      {sectionTabs}

      {!profile ? (
        /*
         * Never invited, or invited and never started. Both read the same from here and the
         * employee's next step is the same either way, so they are not distinguished.
         */
        <p className="myinfo__empty">
          You have not submitted your employee information yet. HR sends a link by email when it
          is your turn — if you were expecting one, ask them to resend it.
        </p>
      ) : (
        <>
          {/*
            * Whose record this is.
            *
            * Every field here comes from the signed-in user, which the page already holds — no
            * second request, and nothing the profile record itself owns. Designation is
            * deliberately absent: it is in the mockup but not on the User type, and a blank
            * labelled slot is worse than one fewer fact.
            *
            * The page opened straight into Personal information before this, with the employee's
            * own name appearing nowhere on it.
            */}
          <div className="eps__card myinfo__identity">
            <span className="eps__avatar eps__avatar--lg" aria-hidden="true">{initials}</span>

            <div className="myinfo__identity-main">
              <span className="myinfo__identity-name">{user?.name || email}</span>
              <span className="myinfo__identity-mail">{email}</span>
            </div>

            <dl className="myinfo__facts">
              <div>
                <dt>Employee ID</dt>
                <dd>{user?.employeeId || '—'}</dd>
              </div>
              <div>
                <dt>Department</dt>
                <dd>{user?.department || '—'}</dd>
              </div>
              <div>
                <dt>Submitted</dt>
                <dd>
                  {profile.submittedAt
                    ? new Date(profile.submittedAt).toLocaleDateString(undefined, {
                        day: 'numeric',
                        month: 'short',
                        year: 'numeric',
                      })
                    : '—'}
                </dd>
              </div>
            </dl>
          </div>

          {/*
            * What is already waiting on HR, named field by field.
            *
            * Shown ABOVE the record and separately from it, because a proposed value must never
            * read as approved information. The sections below keep showing what the company
            * actually holds; this says what has been asked for and is not yet agreed.
            */}
          {pending.length > 0 && !editing && (
            <div className="myinfo__pending">
              <strong>
                {pending.length} change{pending.length === 1 ? '' : 's'} waiting for HR approval
              </strong>
              <ul>
                {pending.map((change) => (
                  <li key={change.id}>
                    {change.label || change.path}: “{change.oldValue || '—'}” → “
                    {change.newValue || '—'}” · {CHANGE_STATUS_LABEL[ChangeRequestStatus.PENDING]}
                  </li>
                ))}
              </ul>
            </div>
          )}

          <div className={editing ? 'myinfo__note myinfo__note--edit' : 'myinfo__note'}>
            {/* Inline SVG, as everywhere else in this codebase — lucide-react is a dependency
                but no component imports it, and one page adopting a second icon convention is
                how a product ends up with two of everything. */}
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
                 strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <circle cx="12" cy="12" r="10" />
              <line x1="12" y1="16" x2="12" y2="12" />
              <line x1="12" y1="8" x2="12.01" y2="8" />
            </svg>
            <p>
              {editing
                ? 'Change what is wrong and send it to HR. Nothing below changes until they ' +
                  'approve it — this is a record of what you declared, so a correction is agreed ' +
                  'rather than simply made.'
                : 'These are the details currently held by the company, based on what you ' +
                  'submitted. If anything is incorrect, use Request changes — HR reviews each ' +
                  'one before it takes effect.'}
            </p>
          </div>

          <ProfileSections
            profile={profile}
            source={editing ? draft : profile}
            editing={editing}
            onChange={(path, value) =>
              setDraft((current) => withPath(current || {}, path, value))
            }
            onRowsChange={(key, rows) =>
              setDraft((current) => ({ ...(current || {}), [key]: rows }))
            }
            revealed={revealed}
            onToggleReveal={() => setRevealed((v) => !v)}
          />
        </>
      )}
    </div>
  );
};

/**
 * The same pill Settings uses, so one status does not read two ways in one product.
 *
 * Tones come from the shared stylesheet rather than a second colour table here — a duplicated
 * palette is how two screens end up disagreeing about what 'Submitted' looks like.
 */
const PILL_TONE: Record<ProfileCompletionStatus, string> = {
  [ProfileCompletionStatus.PENDING]: 'grey',
  [ProfileCompletionStatus.INVITATION_SENT]: 'amber',
  [ProfileCompletionStatus.IN_PROGRESS]: 'blue',
  [ProfileCompletionStatus.SUBMITTED]: 'amber',
  [ProfileCompletionStatus.CHANGES_PENDING_APPROVAL]: 'amber',
  [ProfileCompletionStatus.COMPLETED]: 'green',
};

const StatusPill: React.FC<{ status?: ProfileCompletionStatus }> = ({ status }) => {
  const value = status || ProfileCompletionStatus.PENDING;
  return (
    <span className={`eps__pill eps__pill--${PILL_TONE[value]}`}>
      {PROFILE_STATUS_LABEL[value]}
    </span>
  );
};

export default MyInformation;
